import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
}

const variantStyles: Record<string, React.CSSProperties> = {
  primary: {
    backgroundColor: '#00a3cc',
    color: '#ffffff',
    border: 'none',
  },
  secondary: {
    backgroundColor: '#003b7a',
    color: '#ffffff',
    border: 'none',
  },
  outline: {
    backgroundColor: 'transparent',
    color: '#003b7a',
    border: '2px solid #003b7a',
  },
  ghost: {
    backgroundColor: 'transparent',
    color: '#334155',
    border: 'none',
  },
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', isLoading, disabled, children, style, ...props }, ref) => {
    const baseStyles = cn(
      'inline-flex items-center justify-center font-semibold',
      'transition-all',
      'disabled:pointer-events-none disabled:opacity-50',
      'active:scale-[0.98]'
    )

    const sizes = {
      sm: 'h-9 px-4 text-sm rounded-md',
      md: 'h-11 px-5 text-base rounded-md',
      lg: 'h-12 px-8 text-lg rounded-md',
    }

    return (
      <button
        ref={ref}
        className={cn(baseStyles, sizes[size], className)}
        style={{ ...variantStyles[variant], ...style }}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </>
        ) : (
          children
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
